/* ============================================================================
 * DASHBOARD LAYOUT SERVICE - REFACTORED
 * Main orchestrator that coordinates all dashboard layout modules
 * ============================================================================ */

import { log } from '@/shared/utils/logger';

// Import types
import { 
  DashboardLayouts, 
  ViewModeLayouts,
  LayoutsWithWidgetData,
  NamedLayout,
  ServiceConfig,
  Widget,
  normalizeWidgetArray,
} from './dashboardLayoutTypes';

// Import constants
import { 
  DEFAULT_CACHE_TIMEOUT, 
  DEFAULT_RETRY_ATTEMPTS, 
  DEFAULT_RETRY_DELAY,
  STORAGE_KEYS,
  VIEW_MODES 
} from './dashboardLayoutConstants';

// Import storage operations
import {
  saveToLocalStorage,
  loadFromLocalStorage,
  markLocalStorageAsSynced,
  markLocalStorageAsPending,
  isLayoutSyncedLocally as checkLayoutSynced,
  clearLocalStorageLayout
} from './dashboardLayoutStorage';

// Import cache operations
import { DashboardLayoutCache } from './dashboardLayoutCache';

// Import API operations
import {
  fetchLayouts,
  saveLayoutToServer,
  resetLayoutOnServer,
  fetchNamedLayouts,
  saveNamedLayoutToServer,
  updateNamedLayoutToServer,
  loadNamedLayoutFromServer,
  deleteNamedLayoutOnServer,
  setDefaultLayoutOnServer
} from './dashboardLayoutApi';

// Import sync operations
import { getSyncStatus as getStatus } from '@/features/projects/services/dashboardLayoutSync';

// Import hook
import { useDashboardLayoutService as createHook } from './dashboardLayoutHook';

/* ============================================================================
 * MAIN SERVICE CLASS
 * ============================================================================ */

class DashboardLayoutService {
  private cache: DashboardLayoutCache;
  private config: ServiceConfig;
  private lastSuccessfulSaveSignatureByViewMode: Map<string, string>;
  private inFlightSavesByViewMode: Map<string, { signature: string; generation: number; promise: Promise<boolean> }>;
  private saveGenerationsByViewMode: Map<string, number>;
  private resettingKeys = new Set<string>();
  private saveChainsByViewMode: Map<string, Promise<void>>;

  constructor() {
    this.config = {
      cacheTimeout: DEFAULT_CACHE_TIMEOUT,
      retryAttempts: DEFAULT_RETRY_ATTEMPTS,
      retryDelay: DEFAULT_RETRY_DELAY
    };
    this.cache = new DashboardLayoutCache(this.config.cacheTimeout);
    this.lastSuccessfulSaveSignatureByViewMode = new Map();
    this.inFlightSavesByViewMode = new Map();
    this.saveGenerationsByViewMode = new Map();
    this.saveChainsByViewMode = new Map();
  }

  private cloneValue<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }

  private currentOwner(): string {
    return this.cache.resolveOwner();
  }

  private saveKey(viewMode: string, owner: string): string {
    return `${owner}:${viewMode}`;
  }

  private isCurrentOwner(owner: string): boolean {
    return this.currentOwner() === owner;
  }

  private persistLocalWidgetData(viewMode: string, widgetData: Widget[] | undefined, owner: string): void {
    if (!this.isCurrentOwner(owner)) return;
    const localData = loadFromLocalStorage(viewMode);
    if (!localData) return;

    const persisted: Record<string, unknown> = {
      layouts: localData.layouts,
      lastModified: localData.lastModified,
      syncStatus: localData.syncStatus,
    };
    if (widgetData) persisted.widgetData = widgetData;

    try {
      localStorage.setItem(STORAGE_KEYS.LAYOUT(viewMode), JSON.stringify(persisted));
    } catch (error) {
      log.error('DashboardLayoutService', 'Failed to persist dashboard widget data:', error);
    }
  }

  private rehydratePendingLocalChanges(owner: string): void {
    if (!this.isCurrentOwner(owner)) return;

    for (const viewMode of VIEW_MODES) {
      const localData = loadFromLocalStorage(viewMode);
      if (!localData || localData.syncStatus !== 'pending') continue;
      const widgetData = normalizeWidgetArray(Reflect.get(localData, 'widgetData')) || undefined;
      this.cache.addPendingChange(viewMode, localData.layouts, widgetData, owner);
    }
  }

  private invalidateSuccessfulSaveBaselines(owner: string, except: ReadonlySet<string> = new Set()): void {
    for (const viewMode of VIEW_MODES) {
      if (except.has(viewMode)) continue;
      this.lastSuccessfulSaveSignatureByViewMode.delete(this.saveKey(viewMode, owner));
    }
  }

  /* ==========================================
   * CORE LAYOUT OPERATIONS
   * ========================================== */

  /**
   * Get user's dashboard layouts from server with caching
   */
  async getLayouts(): Promise<LayoutsWithWidgetData> {
    const owner = this.currentOwner();
    try {
      // Check cache first
      if (this.cache.isCacheValid(owner)) {
        const cachedLayouts = this.cache.getCachedLayouts(owner);
        if (cachedLayouts) {
          const widgetData = this.cache.getCachedWidgetData(owner);
          return widgetData ? { layouts: cachedLayouts, widgetData } : { layouts: cachedLayouts };
        }
      }

      // A process reload clears the in-memory pending queue, but the scoped
      // local record remains the user's newest intent. Rehydrate it before an
      // authoritative fetch so stale server data cannot replace it.
      this.rehydratePendingLocalChanges(owner);

      // Fetch from server first - server is the source of truth
      const saveGenerationsAtFetch = new Map(
        VIEW_MODES.map((viewMode) => [viewMode, this.saveGenerationsByViewMode.get(this.saveKey(viewMode, owner)) ?? 0]),
      );
      const { layouts, lastModified, widgetData } = await fetchLayouts();
      if (!this.isCurrentOwner(owner)) return { layouts: {} };
      const modesSavedDuringFetch = new Set<string>(
        VIEW_MODES.filter((viewMode) =>
          (this.saveGenerationsByViewMode.get(this.saveKey(viewMode, owner)) ?? 0) !== saveGenerationsAtFetch.get(viewMode),
        ),
      );
      this.invalidateSuccessfulSaveBaselines(owner, modesSavedDuringFetch);
      
      // If server has data, use it
      if (Object.keys(layouts).length > 0) {
        const acceptedLayouts = { ...layouts };
        const acceptedLastModified = { ...lastModified };
        let acceptedWidgetData = widgetData ? { ...widgetData } : undefined;
        const currentCache = this.cache.getCacheState(owner);
        const currentWidgetData = this.cache.getCachedWidgetData(owner);
        for (const viewMode of modesSavedDuringFetch) {
          const localData = loadFromLocalStorage(viewMode);
          const currentLayout = localData?.syncStatus === 'pending'
            ? localData.layouts
            : currentCache?.layouts[viewMode];
          if (currentLayout) acceptedLayouts[viewMode] = currentLayout;
          else delete acceptedLayouts[viewMode];

          const currentModified = currentCache?.lastModified[viewMode];
          if (currentModified) acceptedLastModified[viewMode] = currentModified;
          else delete acceptedLastModified[viewMode];

          const localWidgets = localData?.syncStatus === 'pending'
            ? normalizeWidgetArray(Reflect.get(localData, 'widgetData')) || undefined
            : undefined;
          const currentWidgets = localWidgets || currentWidgetData?.[viewMode];
          if (currentWidgets) {
            acceptedWidgetData = acceptedWidgetData || {};
            acceptedWidgetData[viewMode] = currentWidgets;
          } else if (acceptedWidgetData) {
            delete acceptedWidgetData[viewMode];
          }
        }

        // Update cache
        if (this.isCurrentOwner(owner)) {
          this.cache.updateCache(acceptedLayouts, acceptedLastModified, acceptedWidgetData, owner);

          // Sync to localStorage for offline support
          for (const viewMode of Object.keys(acceptedLayouts)) {
            if (modesSavedDuringFetch.has(viewMode)) continue;
            if (this.cache.getPendingChanges(owner)[viewMode]) continue;
            saveToLocalStorage(viewMode, acceptedLayouts[viewMode]);
            this.persistLocalWidgetData(viewMode, acceptedWidgetData?.[viewMode], owner);
            markLocalStorageAsSynced(viewMode);
          }
        }
        
        return { layouts: this.cache.getCachedLayouts(owner) || {}, widgetData: this.cache.getCachedWidgetData(owner) };
      }
      
      // Only fall back to localStorage if server has no data
      if (!this.isCurrentOwner(owner)) {
        const cachedLayouts = this.cache.getCachedLayouts(owner);
        if (cachedLayouts && Object.keys(cachedLayouts).length > 0) {
          const widgetData = this.cache.getCachedWidgetData(owner);
          return widgetData ? { layouts: cachedLayouts, widgetData } : { layouts: cachedLayouts };
        }
        return { layouts: {} };
      }
      const pendingLayouts = this.cache.getCachedLayouts(owner);
      if (pendingLayouts && (this.cache.hasPendingChanges(owner) || modesSavedDuringFetch.size > 0)) {
        const pendingWidgetData = this.cache.getCachedWidgetData(owner);
        return pendingWidgetData
          ? { layouts: pendingLayouts, widgetData: pendingWidgetData }
          : { layouts: pendingLayouts };
      }

      const localLayouts: ViewModeLayouts = {};
      for (const viewMode of VIEW_MODES) {
        const localData = loadFromLocalStorage(viewMode);
        if (localData) {
          localLayouts[viewMode] = localData.layouts;
        }
      }
      
      return Object.keys(localLayouts).length > 0 ? { layouts: localLayouts } : { layouts: {} };

    } catch {
      if (!this.isCurrentOwner(owner)) return { layouts: {} };
      // Return cached data if available during error
      const cachedLayouts = this.cache.getCachedLayouts(owner);
      if (cachedLayouts) {
        const widgetData = this.cache.getCachedWidgetData(owner);
        return widgetData ? { layouts: cachedLayouts, widgetData } : { layouts: cachedLayouts };
      }
      
      if (!this.isCurrentOwner(owner)) {
        return { layouts: {} };
      }

      // Try localStorage data as fallback
      const localLayouts: ViewModeLayouts = {};
      for (const viewMode of VIEW_MODES) {
        const localData = loadFromLocalStorage(viewMode);
        if (localData) {
          localLayouts[viewMode] = localData.layouts;
        }
      }
      
      return Object.keys(localLayouts).length > 0 ? { layouts: localLayouts } : { layouts: {} };
    }
  }

  /** Retain the complete latest edit before the provider's debounce fires. */
  stageLayout(viewMode: string, layouts: DashboardLayouts, widgets?: Widget[]): void {
    const owner = this.currentOwner();
    const key = this.saveKey(viewMode, owner);
    if (this.resettingKeys.has(key)) return;
    this.saveGenerationsByViewMode.set(key, (this.saveGenerationsByViewMode.get(key) ?? 0) + 1);
    this.lastSuccessfulSaveSignatureByViewMode.delete(key);
    saveToLocalStorage(viewMode, layouts);
    this.persistLocalWidgetData(viewMode, widgets, owner);
    this.cache.addPendingChange(viewMode, layouts, widgets, owner);
  }

  /**
   * Save layout for specific view mode
   */
  async saveLayout(viewMode: string, layouts: DashboardLayouts, widgetData?: Widget[]): Promise<boolean> {
    const owner = this.currentOwner();
    if (this.resettingKeys.has(this.saveKey(viewMode, owner))) return false;
    // Always save to localStorage first for immediate persistence
    saveToLocalStorage(viewMode, layouts);

    const widgets = Array.isArray(widgetData) ? widgetData : undefined;
    this.persistLocalWidgetData(viewMode, widgets, owner);
    const saveSignature = JSON.stringify({ layouts, widgetData: widgets || [] });
    const saveKey = this.saveKey(viewMode, owner);
    const lastSuccessfulSignature = this.lastSuccessfulSaveSignatureByViewMode.get(saveKey);
    if (lastSuccessfulSignature === saveSignature && !this.inFlightSavesByViewMode.has(saveKey)) {
      return true;
    }

    const inFlightSave = this.inFlightSavesByViewMode.get(saveKey);
    if (inFlightSave && inFlightSave.signature === saveSignature &&
        inFlightSave.generation === this.saveGenerationsByViewMode.get(saveKey)) {
      return inFlightSave.promise;
    }

    const generation = (this.saveGenerationsByViewMode.get(saveKey) ?? 0) + 1;
    this.saveGenerationsByViewMode.set(saveKey, generation);
    const previousChain = this.saveChainsByViewMode.get(saveKey) ?? Promise.resolve();
    let releaseChain!: () => void;
    const chainGate = new Promise<void>((resolve) => {
      releaseChain = resolve;
    });
    this.saveChainsByViewMode.set(saveKey, previousChain.then(() => chainGate));

    const savePromise = (async (): Promise<boolean> => {
      await previousChain;
      try {
        if (this.saveGenerationsByViewMode.get(saveKey) !== generation) {
          return false;
        }

        if (!this.isCurrentOwner(owner)) {
          this.cache.addPendingChange(viewMode, layouts, widgets, owner);
          return false;
        }

        // Then attempt to sync to cloud
        const { success, lastModified } = await saveLayoutToServer(viewMode, layouts, widgets);

        if (success) {
          if (this.saveGenerationsByViewMode.get(saveKey) === generation) {
            this.cache.updateViewModeLayout(viewMode, layouts, lastModified, widgets, owner);
            this.lastSuccessfulSaveSignatureByViewMode.set(saveKey, saveSignature);
            if (this.isCurrentOwner(owner)) {
              markLocalStorageAsSynced(viewMode);
            }
          }

          return true;
        }

        if (this.saveGenerationsByViewMode.get(saveKey) === generation) {
          this.cache.addPendingChange(viewMode, layouts, widgets, owner);
          if (this.isCurrentOwner(owner)) {
            markLocalStorageAsPending(viewMode);
          }
        }

        return false;

      } catch (error) {
        log.error('DashboardLayoutService', 'Dashboard layout save error:', error);

        // Cache pending changes for offline sync
        if (this.saveGenerationsByViewMode.get(saveKey) === generation) {
          this.cache.addPendingChange(viewMode, layouts, widgets, owner);
          if (this.isCurrentOwner(owner)) {
            markLocalStorageAsPending(viewMode);
          }
        }

        // Re-throw specific errors to provide better user feedback
        if (error instanceof Error) {
          if (error.message.includes('Authentication required')) {
            throw new Error('Please log in again to save your layout');
          }
          if (error.message.includes('Access denied')) {
            throw new Error('You don\'t have permission to save this layout');
          }
          if (error.message.includes('Failed to save layout')) {
            throw new Error('Unable to save layout. Please check your connection and try again.');
          }
        }

        return false;
      } finally {
        const currentInFlightSave = this.inFlightSavesByViewMode.get(saveKey);
        if (currentInFlightSave?.generation === generation) {
          this.inFlightSavesByViewMode.delete(saveKey);
        }
        releaseChain();
      }
    })();

    this.inFlightSavesByViewMode.set(saveKey, { signature: saveSignature, generation, promise: savePromise });
    return savePromise;
  }

  /**
   * Reset layout to default for specific view mode
   */
  async resetLayout(viewMode: string): Promise<boolean> {
    const owner = this.currentOwner();
    const resetKey = this.saveKey(viewMode, owner);
    if (this.resettingKeys.has(resetKey)) return false;
    this.resettingKeys.add(resetKey);
    // Invalidate queued saves now, then let the already-dispatched write finish
    // before deleting. Autosave callbacks during the reset cannot recreate it.
    const generation = (this.saveGenerationsByViewMode.get(resetKey) ?? 0) + 1;
    this.saveGenerationsByViewMode.set(resetKey, generation);
    this.lastSuccessfulSaveSignatureByViewMode.delete(resetKey);
    const previousChain = this.saveChainsByViewMode.get(resetKey) ?? Promise.resolve();
    let releaseChain!: () => void;
    const gate = new Promise<void>(resolve => { releaseChain = resolve; });
    this.saveChainsByViewMode.set(resetKey, previousChain.then(() => gate));
    try {
      await previousChain;
      if (!this.isCurrentOwner(owner)) return false;
      const success = await resetLayoutOnServer(viewMode);
      if (success) {
        this.cache.clearViewMode(viewMode, owner);
        if (this.isCurrentOwner(owner)) clearLocalStorageLayout(viewMode);
      } else if (this.isCurrentOwner(owner)) {
        markLocalStorageAsPending(viewMode);
        this.rehydratePendingLocalChanges(owner);
      }
      return success;
    } catch (error) {
      if (this.isCurrentOwner(owner)) {
        markLocalStorageAsPending(viewMode);
        this.rehydratePendingLocalChanges(owner);
      }
      log.error('DashboardLayoutService', 'Failed to reset layout:', error);
      return false;
    } finally {
      this.resettingKeys.delete(resetKey);
      releaseChain();
    }
  }

  /* ==========================================
   * SYNC AND MIGRATION OPERATIONS
   * ========================================== */

  /**
   * Sync pending changes when back online
   */
  async syncPendingChanges(): Promise<void> {
    const owner = this.currentOwner();
    const pendingChanges = this.cache.getPendingChanges(owner);
    const pendingWidgetData = this.cache.getPendingWidgetData(owner);
    if (Object.keys(pendingChanges).length === 0) {
      return;
    }

    await Promise.allSettled(
      Object.entries(pendingChanges).map(([viewMode, layouts]) =>
        this.saveLayout(viewMode, layouts, pendingWidgetData[viewMode])
      )
    );
  }

  /* ==========================================
   * STATUS OPERATIONS
   * ========================================== */

  /**
   * Check if user has pending changes
   */
  hasPendingChanges(): boolean {
    return this.cache.hasPendingChanges(this.currentOwner());
  }

  /**
   * Get sync status
   */
  getSyncStatus(): 'synced' | 'pending' | 'offline' {
    return getStatus(this.hasPendingChanges());
  }

  /**
   * Check if a layout is synced locally
   */
  isLayoutSyncedLocally(viewMode: string, layoutId: string): boolean {
    return checkLayoutSynced(viewMode, layoutId);
  }

  /* ==========================================
   * NAMED LAYOUT OPERATIONS
   * ========================================== */

  /**
   * Get named layouts for a view mode
   */
  async getNamedLayouts(viewMode: string): Promise<NamedLayout[]> {
    const owner = this.currentOwner();
    try {
      // Check cache first
      const cachedLayouts = this.cache.getCachedNamedLayouts(viewMode, owner);
      if (cachedLayouts) {
        return this.cloneValue(cachedLayouts);
      }

      const namedLayouts = await fetchNamedLayouts(viewMode);
      
      // Update cache
      this.cache.updateNamedLayouts(viewMode, this.cloneValue(namedLayouts), owner);
      
      return this.cloneValue(namedLayouts);
    } catch (error) {
      log.error('DashboardLayoutService', 'Failed to fetch named layouts:', error);
      // An empty array is authoritative only when the server successfully
      // returned no layouts. Propagate transport/auth failures so callers do
      // not erase a persisted current-layout label on a transient outage.
      throw error;
    }
  }

  /**
   * Save a named layout
   */
  async saveNamedLayout(
    viewMode: string, 
    name: string, 
    layouts: DashboardLayouts, 
    isDefault = false,
    widgetData?: Widget[]
  ): Promise<NamedLayout | null> {
    const owner = this.currentOwner();
    try {
      const savedLayout = await saveNamedLayoutToServer(viewMode, name, layouts, widgetData);
      
      // Update cache only on successful save
      this.cache.addNamedLayout(viewMode, this.cloneValue(savedLayout), isDefault, owner);
      
      return this.cloneValue(savedLayout);
    } catch (error) {
      // Log error but re-throw to ensure UI receives the error
      log.error('DashboardLayoutService', 'Failed to save named layout:', error);
      
      // Re-throw with a user-friendly message if it's a generic error
      if (error instanceof Error) {
        throw error;  // Re-throw the original error with its message
      } else {
        throw new Error('Failed to save layout. Please check your connection and try again.');
      }
    }
  }

  /**
   * Update an existing named layout
   */
  async updateNamedLayout(
    viewMode: string,
    layoutId: string,
    name: string,
    layouts: DashboardLayouts,
    isDefault = false,
    widgetData?: Widget[]
  ): Promise<NamedLayout | null> {
    const owner = this.currentOwner();
    try {
      const updatedLayout = await updateNamedLayoutToServer(
        viewMode,
        layoutId,
        name,
        layouts,
        widgetData,
        isDefault
      );

      this.cache.updateNamedLayout(viewMode, this.cloneValue(updatedLayout), owner);

      return this.cloneValue(updatedLayout);
    } catch (error) {
      log.error('DashboardLayoutService', 'Failed to update named layout:', error);

      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('Failed to update layout. Please check your connection and try again.');
      }
    }
  }

  /**
   * Load a named layout
   */
  async loadNamedLayout(viewMode: string, layoutId: string): Promise<DashboardLayouts | null> {
    const owner = this.currentOwner();
    try {
      // Check cache first
      const cachedLayouts = this.cache.getCachedNamedLayouts(viewMode, owner);
      const cachedLayout = cachedLayouts?.find(l => l.id === layoutId);
      if (cachedLayout) {
        return this.cloneValue(cachedLayout.layouts);
      }

      const layouts = await loadNamedLayoutFromServer(viewMode, layoutId);
      return layouts ? this.cloneValue(layouts) : null;
    } catch (error) {
      log.error('DashboardLayoutService', 'Failed to load named layout:', error);
      return null;
    }
  }

  /**
   * Delete a named layout
   */
  async deleteNamedLayout(viewMode: string, layoutId: string): Promise<boolean> {
    const owner = this.currentOwner();
    try {
      const success = await deleteNamedLayoutOnServer(viewMode, layoutId);
      
      if (success) {
        // Update cache
        this.cache.removeNamedLayout(viewMode, layoutId, owner);
      }
      
      return success;
    } catch (error) {
      log.error('DashboardLayoutService', 'Failed to delete named layout:', error);
      return false;
    }
  }

  /**
   * Set a named layout as default
   */
  async setDefaultLayout(viewMode: string, layoutId: string): Promise<boolean> {
    const owner = this.currentOwner();
    try {
      const success = await setDefaultLayoutOnServer(viewMode, layoutId);
      
      if (success) {
        // Update cache
        this.cache.setDefaultLayout(viewMode, layoutId, owner);
      }
      
      return success;
    } catch (error) {
      log.error('DashboardLayoutService', 'Failed to set default layout:', error);
      return false;
    }
  }

  /* ==========================================
   * CACHE OPERATIONS
   * ========================================== */

  /**
   * Clear cache (useful for logout)
   */
  clearCache(): void {
    this.cache.clearCache();
    this.lastSuccessfulSaveSignatureByViewMode.clear();
    this.inFlightSavesByViewMode.clear();
    this.saveGenerationsByViewMode.clear();
    this.saveChainsByViewMode.clear();
  }
}

/* ============================================================================
 * SINGLETON INSTANCE & EXPORTS
 * ============================================================================ */

// Singleton instance
export const dashboardLayoutService = new DashboardLayoutService();

// React hook for easy integration
export function useDashboardLayoutService() {
  return createHook(dashboardLayoutService);
}

// Re-export types for convenience
export type { 
  DashboardLayouts,
  ViewModeLayouts,
  NamedLayout,
  LayoutResponse,
  SaveLayoutRequest,
  SaveLayoutResponse,
  ViewModeNamedLayouts,
  LayoutCache,
  ServiceConfig,
  Widget,
  WidgetPosition,
  WidgetConfig,
  GridConfig,
  LocalStorageLayoutData
} from './dashboardLayoutTypes';

export type { ViewMode, SyncStatus } from './dashboardLayoutConstants';
